import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { SEO } from '../components/SEO';

function PrijestopnaGodina() {
  const [year, setYear] = useState<string>('');
  const [result, setResult] = useState<{
    isLeapYear: boolean;
    nextLeapYear: number;
    yearsUntilNext: number;
    explanation: string;
  } | null>(null);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Prijestupne Godine",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Saznajte je li određena godina prijestupna. Jednostavan kalkulator za provjeru prijestupnih godina s objašnjenjima i dodatnim informacijama.",
    "url": "https://online-kalkulator.com/prijestopna-godina",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Provjera prijestupne godine",
      "Objašnjenje rezultata",
      "Sljedeća prijestupna godina",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  const isLeapYear = (year: number): boolean => {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  };

  const findNextLeapYear = (currentYear: number): number => {
    let nextYear = currentYear + 1;
    while (!isLeapYear(nextYear)) {
      nextYear++;
    }
    return nextYear;
  };

  const getExplanation = (year: number, isLeap: boolean): string => {
    if (isLeap) {
      if (year % 400 === 0) {
        return `${year}. je prijestupna godina jer je djeljiva s 400.`;
      }
      return `${year}. je prijestupna godina jer je djeljiva s 4 i nije djeljiva sa 100.`;
    }
    if (year % 100 === 0) {
      return `${year}. nije prijestupna godina jer je djeljiva sa 100, ali nije djeljiva s 400.`;
    }
    return `${year}. nije prijestupna godina jer nije djeljiva s 4.`;
  };

  const calculateLeapYear = () => {
    const yearNum = parseInt(year);
    if (isNaN(yearNum)) return;

    const leapYear = isLeapYear(yearNum);
    const nextLeap = leapYear ? findNextLeapYear(yearNum) : findNextLeapYear(yearNum - 1);
    const yearsUntil = nextLeap - yearNum;

    setResult({
      isLeapYear: leapYear,
      nextLeapYear: nextLeap,
      yearsUntilNext: yearsUntil,
      explanation: getExplanation(yearNum, leapYear)
    });
  };

  return (
    <>
      <SEO 
        title="Kalkulator Prijestupne Godine | Izračun Prijestupnih Godina"
        description="Saznajte je li određena godina prijestupna. Jednostavan kalkulator za provjeru prijestupnih godina s objašnjenjima i dodatnim informacijama."
        canonicalUrl="/prijestopna-godina"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calendar className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Prijestupne Godine</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unesite godinu
                </label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="npr. 2024"
                />
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculateLeapYear}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Provjeri
              </button>

              {/* Results */}
              {result && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-gray-50">
                    <p className={`text-lg ${result.isLeapYear ? 'text-green-600' : 'text-blue-600'}`}>
                      {result.explanation}
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Sljedeća prijestupna godina:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {result.nextLeapYear}.
                    </span>
                  </div>

                  {result.yearsUntilNext > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Godine do sljedeće prijestupne:</span>
                      <span className="font-semibold">
                        {result.yearsUntilNext}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što je Prijestupna Godina?</h2>
                <p className="text-gray-600 leading-relaxed">
                  Prijestupna godina je godina koja ima 366 dana umjesto uobičajenih 365 dana.
                  Dodatni dan (29. veljače) dodaje se kako bi se kalendarska godina uskladila
                  sa Sunčevom godinom koja traje približno 365.242199 dana.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Pravila za Prijestupne Godine</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Godina je prijestupna ako je djeljiva s 4</li>
                  <li>Međutim, ako je godina djeljiva sa 100, mora biti djeljiva i s 400 da bi bila prijestupna</li>
                  <li>Na primjer:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>2000. je bila prijestupna (djeljiva s 400)</li>
                      <li>2020. je bila prijestupna (djeljiva s 4)</li>
                      <li>1900. nije bila prijestupna (djeljiva sa 100, ali ne s 400)</li>
                      <li>2100. neće biti prijestupna (djeljiva sa 100, ali ne s 400)</li>
                    </ul>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Zašto su Potrebne Prijestupne Godine?</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Zemlji treba približno 365.242199 dana za jedan obilazak oko Sunca</li>
                  <li>Bez prijestupnih godina, kalendar bi se svake godine pomicao za približno 6 sati</li>
                  <li>Nakon 100 godina, kalendar bi kasnio 24 dana</li>
                  <li>Prijestupne godine održavaju kalendar usklađenim s godišnjim dobima</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti Kalkulator</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite željenu godinu u polje za unos</li>
                  <li>Kliknite na gumb "Provjeri"</li>
                  <li>Kalkulator će pokazati:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Je li godina prijestupna</li>
                      <li>- Objašnjenje zašto jest ili nije prijestupna</li>
                      <li>- Sljedeću prijestupnu godinu</li>
                      <li>- Koliko godina je do sljedeće prijestupne godine</li>
                    </ul>
                  </li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Zanimljivosti</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Prijestupne godine uvedene su u Julijanskom kalendaru 45. godine pr. Kr.</li>
                  <li>Gregorijanski kalendar, koji danas koristimo, uveden je 1582. godine</li>
                  <li>Osobe rođene 29. veljače često slave rođendan 28. veljače ili 1. ožujka</li>
                  <li>Vjerojatnost rođenja na 29. veljače je približno 1/1461</li>
                  <li>Sljedeće prijestupne godine su: 2024, 2028, 2032, 2036, 2040</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PrijestopnaGodina;